import { Button } from '../../primitives/button';
import { LoadingAddon } from '../../primitives/loading-addon';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItemWithSlot,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select';
import { cn } from '../../utils/cn';
import { OptionInfoField, OptionInfoPopover } from '../option-info-popover';

import type { JSX } from 'react';

// What a controversial name xd
export type SelectModelModel = (
  | {
      infoFields?: OptionInfoField[];
      value: string;
      item: string | JSX.Element;
      section?: undefined;
    }
  | { value?: undefined; item?: undefined; section: string }
)[];

interface SelectModelProps
  extends
    React.ComponentPropsWithoutRef<typeof Select>,
    Pick<React.ComponentPropsWithoutRef<typeof SelectValue>, 'placeholder'>,
    Pick<React.ComponentPropsWithoutRef<typeof SelectTrigger>, 'size' | 'variant'> {
  button?: {
    icon?: JSX.Element;
    id?: string;
    onClick: VoidFunction;
    text?: string;
  };
  className?: string;
  id?: string;
  isLoading?: boolean;
  model: SelectModelModel;
  readOnly?: boolean;
}

export function SelectModel({
  button,
  className,
  disabled = false,
  id,
  isLoading = false,
  model,
  placeholder = 'Seleccionar',
  readOnly = false,
  size,
  variant = 'outline',
  ...props
}: SelectModelProps) {
  const selectedValue = typeof props.value === 'string' ? props.value : undefined;

  const selectedOption = selectedValue
    ? model.find((modelItem) => modelItem?.value === selectedValue)
    : undefined;

  const selectedOptionInfoFields =
    selectedOption && selectedOption.value && selectedOption.item
      ? selectedOption.infoFields
      : undefined;

  const hasValues = model.some((item) => {
    const value = item?.value;
    return typeof value === 'string' ? value.trim() !== '' : value != null;
  });

  const disabledSelect = disabled || model.length === 0 || readOnly || !hasValues || isLoading;

  return (
    <div className={cn('relative flex w-full items-center', className)}>
      <Select {...props} disabled={disabledSelect}>
        <SelectTrigger
          className={cn('disabled:text-primary/50 disabled:opacity-100', {
            'rounded-r-none border-r-0': button?.onClick,
            'cursor-not-allowed border-input/50': disabledSelect,
          })}
          id={id}
          size={size}
          variant={variant}
        >
          <div className="inline-flex min-w-0 items-center gap-1">
            <SelectValue
              placeholder={
                model.length === 0
                  ? 'No hay opciones'
                  : hasValues
                    ? placeholder
                    : 'La fuente de datos no contiene valores válidos'
              }
            >
              {selectedOption?.value && selectedOption?.item ? selectedOption.item : null}
            </SelectValue>
            <OptionInfoPopover fields={selectedOptionInfoFields} />
          </div>
        </SelectTrigger>
        {hasValues ? (
          <SelectContent>
            <SelectGroup>
              {model.map((modelItem, index) => {
                if (modelItem?.value && modelItem?.item) {
                  return (
                    <SelectItemWithSlot
                      id={id ? `${id}-${modelItem?.value}` : undefined}
                      key={modelItem.value}
                      rightSlot={<OptionInfoPopover fields={modelItem.infoFields} />}
                      value={modelItem.value}
                    >
                      {modelItem?.item}
                    </SelectItemWithSlot>
                  );
                }
                if (modelItem?.section) {
                  return (
                    <SelectLabel
                      className="text-xs text-muted-foreground"
                      key={modelItem?.section ?? index}
                    >
                      {modelItem?.section}
                    </SelectLabel>
                  );
                }
                return null;
              })}
            </SelectGroup>
          </SelectContent>
        ) : null}
      </Select>
      {button?.onClick ? (
        <Button
          type="button"
          size={size === 'sm' ? 'sm' : 'default'}
          variant="secondary"
          className="rounded-l-none border-l-0 border-input px-4 focus-visible:relative"
          onClick={button.onClick}
        >
          {button?.icon ? button.icon : null}
          {button?.text ? button.text : null}
        </Button>
      ) : null}
      {isLoading && <LoadingAddon type="plain" />}
    </div>
  );
}
